import React, { useMemo, useState } from "react";
import { Calendar as CalIcon, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * priceForDate — pure helper. Given a base price + date_pricing config + ISO date,
 * returns the surcharge/discount details. Exported for reuse.
 */
export function priceForDate(base, dp, isoDate) {
  if (!dp) return { finalPrice: base, appliedPct: 0, appliedLabel: null };
  const festivalHit = (dp.festival_dates || []).find(f => f.date === isoDate);
  if (festivalHit) {
    return {
      finalPrice: Math.round(base * (1 + festivalHit.surcharge_pct / 100)),
      appliedPct: festivalHit.surcharge_pct,
      appliedLabel: festivalHit.label,
    };
  }
  const day = new Date(isoDate + "T12:00:00").getDay();
  if ([5, 6, 0].includes(day)) {
    const pct = dp.weekend_surcharge_pct || 15;
    return { finalPrice: Math.round(base * (1 + pct / 100)), appliedPct: pct, appliedLabel: "Weekend rate" };
  }
  if (day >= 1 && day <= 3 && dp.off_season_discount_pct) {
    const pct = dp.off_season_discount_pct;
    return { finalPrice: Math.round(base * (1 - pct / 100)), appliedPct: -pct, appliedLabel: "Mid-week saver" };
  }
  return { finalPrice: base, appliedPct: 0, appliedLabel: "Standard rate" };
}

/**
 * cheapestDateWithin — scans the next N days and returns the lowest-priced option.
 */
export function cheapestDateWithin(base, dp, days = 30) {
  if (!dp) return null;
  const today = new Date();
  let best = null;
  for (let i = 1; i <= days; i++) {
    const d = new Date(today.getTime() + i * 86400000);
    const iso = d.toISOString().slice(0, 10);
    const p = priceForDate(base, dp, iso);
    if (!best || p.finalPrice < best.finalPrice) best = { ...p, isoDate: iso };
  }
  return best;
}

/**
 * DatePriceCalendar — reusable date picker + surcharge/discount visualisation
 * + "cheapest date within 30 days" auto-suggester.
 *
 * Props:
 *  - basePrice: number
 *  - datePricing: { weekend_surcharge_pct, off_season_discount_pct, festival_dates }
 *  - startDate, endDate: ISO strings
 *  - onChange: ({startDate, endDate, finalPrice, appliedPct, appliedLabel}) => void
 *  - labelPrefix: "Check-in" | "Start date" | "Event date"
 *  - showRange: bool (hotel needs check-in + check-out)
 */
export default function DatePriceCalendar({
  basePrice,
  datePricing,
  startDate,
  endDate,
  onChange,
  labelPrefix = "Start date",
  showRange = false,
  testId = "date-price",
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [start, setStart] = useState(startDate || (() => {
    const d = new Date(Date.now() + 7 * 86400000); return d.toISOString().slice(0, 10);
  }));
  const [end, setEnd] = useState(endDate || (() => {
    const d = new Date(Date.now() + 8 * 86400000); return d.toISOString().slice(0, 10);
  }));

  const pricing = useMemo(() => priceForDate(basePrice, datePricing, start), [basePrice, datePricing, start]);
  const cheapest = useMemo(() => cheapestDateWithin(basePrice, datePricing, 30), [basePrice, datePricing]);
  const savings = cheapest ? pricing.finalPrice - cheapest.finalPrice : 0;

  const notify = (s, e, p) => onChange?.({ startDate: s, endDate: e, ...p });

  const set = (s, e) => {
    setStart(s);
    if (showRange) setEnd(e);
    notify(s, e, priceForDate(basePrice, datePricing, s));
  };

  const applyCheapest = () => {
    if (!cheapest) return;
    const newEnd = showRange
      ? new Date(new Date(cheapest.isoDate).getTime() + 86400000).toISOString().slice(0, 10)
      : end;
    set(cheapest.isoDate, newEnd);
  };

  return (
    <div className="rounded-xl border border-stone-200 p-3" data-testid={testId}>
      <label className="text-[11px] uppercase tracking-widest font-bold text-stone-500">
        {labelPrefix}{showRange ? "" : ""}
      </label>

      {showRange ? (
        <div className="mt-1.5 grid grid-cols-2 gap-2">
          <div>
            <div className="text-[10px] font-bold text-stone-500 mb-0.5">Check-in</div>
            <Input type="date" value={start} min={today} onChange={e => set(e.target.value, end)} className="rounded-xl" data-testid={`${testId}-checkin`} />
          </div>
          <div>
            <div className="text-[10px] font-bold text-stone-500 mb-0.5">Check-out</div>
            <Input type="date" value={end} min={start} onChange={e => set(start, e.target.value)} className="rounded-xl" data-testid={`${testId}-checkout`} />
          </div>
        </div>
      ) : (
        <Input
          type="date" value={start} min={today}
          onChange={e => set(e.target.value, end)}
          className="mt-1.5 rounded-xl"
          data-testid={`${testId}-input`}
        />
      )}

      {pricing.appliedLabel && (
        <div
          className={`mt-2 text-[11px] font-bold px-2.5 py-1 rounded-full inline-block ${
            pricing.appliedPct > 0
              ? "bg-rose-50 text-rose-800 border border-rose-200"
              : pricing.appliedPct < 0
                ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                : "bg-stone-100 text-stone-700 border border-stone-200"
          }`}
          data-testid={`${testId}-badge`}
        >
          {pricing.appliedLabel}{pricing.appliedPct !== 0 && ` · ${pricing.appliedPct > 0 ? "+" : ""}${pricing.appliedPct}%`}
        </div>
      )}

      {/* Cheapest-date auto-suggester */}
      {cheapest && cheapest.isoDate !== start && savings > 0 && (
        <button
          onClick={applyCheapest}
          className="mt-3 w-full text-left flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50/60 hover:bg-emerald-50 px-3 py-2 transition-colors"
          data-testid={`${testId}-cheapest`}
        >
          <Sparkles size={14} className="text-emerald-700 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700">Cheapest in next 30 days</div>
            <div className="text-sm font-bold text-stone-900">
              {new Date(cheapest.isoDate + "T12:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}
              <span className="text-emerald-700 font-bold"> · Save ₹{savings.toLocaleString("en-IN")}</span>
            </div>
            {cheapest.appliedLabel && <div className="text-[10px] text-stone-500">{cheapest.appliedLabel}</div>}
          </div>
          <CalIcon size={14} className="text-emerald-700" />
        </button>
      )}
    </div>
  );
}
